import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { useHistory, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Step } from "src/screens/process/step/Step";
import { useForm, SubmitHandler } from "react-hook-form";
import { runInAction, toJS } from "mobx";
import { Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import { IMAGE_TYPE, NotificationImage } from "src/api/Notification";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ImageSelector } from "src/components/imageSelector";

interface DocumentQuestionData {
    message: string;
    documentimage?: NotificationImage;
}

const DocumentQuestionBase = () => {
    const { t } = useTranslation();
    const { easyContactStore, uiStore } = useContext(RootStoreContext);
    const history = useHistory();
    const { processId } = useParams<{ processId: string; stepId: string }>();
    const { nextStep } = easyContactStore.stepNavigation;

    const text_schema = yup.object().shape({
        message: yup.string().required(t("form.required_field"))
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        setFocus
    } = useForm<DocumentQuestionData>({
        resolver: yupResolver(text_schema)
    });

    useEffect(() => {
        if (easyContactStore.notification.documentimage) {
            setValue("documentimage", toJS(easyContactStore.notification.documentimage));
        }
    }, [setValue, easyContactStore.notification.documentimage]);

    useEffect(() => {
        setValue("message", easyContactStore.notification.message || "");
        setFocus("message");
    }, [easyContactStore.notification.message, setValue, setFocus]);

    const onSubmit: SubmitHandler<DocumentQuestionData> = useCallback(
        (data: DocumentQuestionData) => {
            runInAction(() => {
                if (data.message) {
                    easyContactStore.setDocumentQuestion(data.message, data.documentimage);
                }
            });
            if (nextStep) {
                history.push(
                    selectRoute(Route.step, { processId, stepType: String(nextStep.type), stepId: nextStep.id })
                );
            }
        },
        [history, easyContactStore, nextStep, processId]
    );

    return (
        <Step>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={uiStore.defaultColumnSize.md}>
                        <UI.Form onSubmit={handleSubmit(onSubmit)}>
                            <UI.Input
                                id="message"
                                {...register("message")}
                                label={`${t("screens.text.message")} *`}
                                type="text"
                                as="textarea"
                                errorMsg={errors.message ? errors.message.message : undefined}
                            />
                            <ImageSelector.Container>
                                <ImageSelector.Item
                                    label={t("screens.text.documentimage")}
                                    name="documentimage"
                                    setValue={setValue}
                                    imagetype={IMAGE_TYPE.DOCUMENT}
                                    initialImageUrl={easyContactStore.notification.documentimage?.url}
                                />
                            </ImageSelector.Container>
                            <UI.Button onClick={handleSubmit(onSubmit)} label={t("labels.continue")} />
                        </UI.Form>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </Step>
    );
};

export const DocumentQuestion = Sentry.withProfiler(observer(DocumentQuestionBase));
